import { formatDateByUserTimeZone } from '../../../helpers/formatDateByUserTimeZone'

/**
 * A refused reopen date, read from the backend's error body.
 *
 * Shared because the same payload now arrives on three paths, and they were drifting apart. The
 * per-section endpoint has always returned it as a 422; the bulk endpoint returns it as a **400 on
 * the request** since Backend #2164's pre-flight validation — every reopen date rule reads only the
 * submitted dates and the project's bounds, so the answer cannot vary across a batch and is decided
 * before a task exists. The third path is the task result's per-section `detail`, handled by
 * `readChangeDetail`, which renders the same information as one line for a toast.
 *
 * Marshmallow keys a field error by the field it failed on, so the sentence is under whichever date
 * was rejected — an early-access refusal says nothing under `endTimestamp`.
 */

interface ReopenErrorBody {
  endTimestamp?: string | string[]
  /** A scheduled reopen refused on its start keys the sentence here, not under `endTimestamp`. */
  startTimestamp?: string | string[]
  earlyAccessReleaseTimestamp_1?: string | string[]
  earlyAccessReleaseTimestamp_2?: string | string[]
  [key: string]: unknown
}

export interface ReopenDateError {
  /** The backend's own sentence, which names the acceptable value inline. */
  message: string
  /** One labelled boundary per line, in the viewer's timezone. */
  detailLines: string[]
}

/** The note the boundary lines carry, because the sentence above them names the same instant in UTC. */
export const LOCAL_TIMEZONE_NOTE = 'Times shown in your local timezone.'

const firstOf = (value: unknown): string | undefined => {
  const candidate = Array.isArray(value) ? value[0] : value
  return typeof candidate === 'string' && candidate.trim().length ? candidate.trim() : undefined
}

/**
 * Every boundary a reopen refusal can carry, labelled to match its rule.
 *
 * `currentEndTimestamp` is on its way out — it comes only from the "24 hours after the previous
 * end" rule, deleted in Backend #2164. It stays until that reaches prod, because until then the
 * rule still fires there and dropping the line would take the date out of a refusal admins can
 * still hit. It renders nothing where the key has already stopped arriving.
 */
const BOUNDARY_LABELS: Array<[string, string]> = [
  ['currentEndTimestamp', 'Previous End Date'],
  ['earliestAllowedEndTimestamp', 'Earliest allowed New End Date'],
  ['latestAllowedEndTimestamp', 'Latest allowed New End Date'],
  ['earliestAllowedStartTimestamp', 'Earliest allowed New Start Date'],
  ['latestAllowedStartTimestamp', 'Latest allowed New Start Date'],
  ['earliestAllowedEarlyAccessReleaseTimestamp_1', 'Earliest allowed Admin Results Release Date'],
  ['latestAllowedEarlyAccessReleaseTimestamp_1', 'Latest allowed Admin Results Release Date'],
  ['earliestAllowedEarlyAccessReleaseTimestamp_2', 'Earliest allowed Instructor Results Release Date'],
  ['latestAllowedEarlyAccessReleaseTimestamp_2', 'Latest allowed Instructor Results Release Date']
]

/**
 * Reads a date refusal out of an error body, or returns `null` if it is not one.
 *
 * `null` matters: a permission failure, a project that is not Live, or a network error must keep
 * their own handling rather than be dressed up as a date problem.
 */
export const readReopenDateError = (data: unknown): ReopenDateError | null => {
  if (data == null || typeof data !== 'object' || Array.isArray(data)) return null
  const body = data as ReopenErrorBody

  // Every field a refusal can be keyed under, in the order the toast's reader uses. `startTimestamp`
  // was missing: a scheduled reopen refused on its start showed the generic fallback, and one that
  // arrived with no boundary key at all was not recognised as a date error at all. Found by review.
  const message = firstOf(body.endTimestamp) ??
    firstOf(body.startTimestamp) ??
    firstOf(body.earlyAccessReleaseTimestamp_1) ??
    firstOf(body.earlyAccessReleaseTimestamp_2)

  const detailLines = BOUNDARY_LABELS.reduce<string[]>((acc, [key, label]) => {
    const formatted = formatDateByUserTimeZone(body[key] as string | null | undefined)
    return formatted === '' ? acc : [...acc, `${label}: ${formatted}`]
  }, [])

  if (message === undefined && detailLines.length === 0) return null
  return { message: message ?? 'That date cannot be used for these course section surveys.', detailLines }
}

/** The refusal as one block of text: sentence, boundary lines, then the timezone note. */
export const formatReopenDateError = (error: ReopenDateError): string =>
  error.detailLines.length
    ? `${error.message}\n\n${error.detailLines.join('\n')}\n\n${LOCAL_TIMEZONE_NOTE}`
    : error.message
